import React, { useState } from 'react';
import { Form, Input, Button, Row, Col, Typography } from 'antd';
import { Box, Link, Alert } from '@mui/material';
import { UserOutlined, LockOutlined, PhoneOutlined, MailOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { hosturl } from '../libs/Constants';
import './style.css';
import Colors from './libs/Colors';
const { Title } = Typography;

const SignUp = () => {
    const [error, setError] = useState(null);
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [otpLoading, setOtpLoading] = useState(false);
    const [otpVerified, setOtpVerified] = useState(false);
    const [showOtpField, setShowOtpField] = useState(false);
    const [otp, setOtp] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [passwordCriteria, setPasswordCriteria] = useState({
        length: false,
        number: false,
        symbol: false,
        uppercase: false,
        lowercase: false,
    });
    const navigate = useNavigate();

    const onPasswordChange = (e) => {
        const password = e.target.value;
        setPasswordCriteria({
            length: password.length >= 8 && password.length <= 50,
            number: /\d/.test(password),
            symbol: /[@$!%*?&]/.test(password),
            uppercase: /[A-Z]/.test(password),
            lowercase: /[a-z]/.test(password),
        });
    };

    const requestOtp = async () => {
        const phoneNumber = form.getFieldValue('phone');
        if (!phoneNumber) {
            setError("Please enter your phone number first.");
            return;
        }

        setOtpLoading(true);
        try {
            const response = await axios.post(`${hosturl}/send-otp`, { phoneNumber });
            if (response.data.verificationId) {
                setError(null);
                setShowOtpField(true);
            }
        } catch (error) {
            setError(error.response?.data?.error || "Failed to send OTP. Please try again.");
        } finally {
            setOtpLoading(false);
        }
    };

    const verifyOtp = async () => {
        if (otp.length !== 6) {
            setError("Please enter a valid 6-digit OTP.");
            return;
        }

        const phoneNumber = form.getFieldValue('phone');
        try {
            const response = await axios.post(`${hosturl}/verify-otp`, { verificationId: phoneNumber, otp });
            if (response.data.success) {
                setOtpVerified(true);
                setError(null);
            } else {
                setError("Invalid OTP. Please try again.");
            }
        } catch (error) {
            setError(error.response?.data?.error || "Failed to verify OTP.");
        }
    };

    const onFinish = async (values) => {
        const { username, email, phone, password, confirm } = values;

        if (!otpVerified) {
            setError("Please verify your phone number with the OTP.");
            return;
        }

        if (password !== confirm) {
            setError("Passwords do not match.");
            return;
        }

        if (Object.values(passwordCriteria).includes(false)) {
            setError("Password does not meet all requirements.");
            return;
        }

        setError('');
        setLoading(true);

        try {
            const response = await axios.post(`${hosturl}/register`, {
                username,
                email,
                phoneNumber: phone,
                password,
            });

            const result = response.data;

            if (response.status === 201) {
                localStorage.setItem('token', result.token);
                setError(null);
                navigate('/home');
            } else {
                setError(result.message || 'An error occurred during registration.');
            }
        } catch (error) {
            setError(error.response?.data?.error || 'Registration failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor:Colors.primaryColor, height: '145vh' }}>
            <div style={{ width: 700, borderRadius: 12, boxShadow: '0 6px 20px rgba(0, 0, 0, 0.1)', padding: '20px', backgroundColor: '#fff', transform: 'translateY(40px)', marginBottom: '30px', animation: 'fadeInUp 1s forwards' }}>
                {error && <Alert severity="error" sx={{ marginBottom: 3, fontSize: '14px' }}>{error}</Alert>}

                <Title level={3} style={{ marginBottom: 16, fontSize: 28, textAlign: 'center' }}>Create an account</Title>
                <Form
                    form={form}
                    name="signup"
                    onFinish={onFinish}
                    layout="vertical"
                    style={{ width: '100%' }}
                >
                    <Row gutter={16}>
                        <Col span={12}>
                            <label htmlFor="username" style={{ display: 'block', fontSize: '14px', marginBottom: '8px' }}>Username</label>
                            <Form.Item
                                name="username"
                                rules={[{ required: true, message: 'Please enter your username!' }]}
                            >
                                <Input
                                    prefix={<UserOutlined />}
                                    placeholder="Username"
                                    style={{ height: 40, borderRadius: 8, padding: '0 10px', marginBottom: '10px', fontSize: '14px', border: '1px solid #dcdfe6', backgroundColor: '#F7F8FA' }}
                                />
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <label htmlFor="phone" style={{ display: 'block', fontSize: '14px', marginBottom: '8px' }}>Phone Number</label>
                            <Form.Item
                                name="phone"
                                rules={[
                                    { required: true, message: 'Please enter your phone number!' },
                                    { len: 10, message: 'Phone number must be 10 digits long!' },
                                ]}
                            >
                                <Input
                                    prefix={<PhoneOutlined />}
                                    placeholder="Phone Number"
                                    maxLength={10}
                                    disabled={otpVerified}
                                    style={{ height: 40, borderRadius: 8, padding: '0 10px', marginBottom: '10px', fontSize: '14px', border: '1px solid #dcdfe6', backgroundColor: '#F7F8FA' }}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Button
                        type="primary"
                        onClick={requestOtp}
                        disabled={otpVerified || otpLoading}
                        loading={otpLoading}
                        style={{ width: '25%', backgroundColor: '#4CAF50', color: '#fff', fontWeight: 'bold', marginBottom: '16px', borderRadius: '8px', height: '40px' }}
                    >
                        {otpVerified ? 'OTP Verified' : 'Send OTP'}
                    </Button>

                    {showOtpField && !otpVerified && (
                        <Form.Item
                            name="otp"
                            label="Enter OTP"
                            rules={[{ required: true, message: 'Please enter the OTP sent to your phone!' }]}
                        >
                            <Input
                                placeholder="OTP"
                                value={otp}
                                onChange={(e) => setOtp(e.target.value)}
                                maxLength={6}
                                onBlur={verifyOtp}
                                style={{ height: 40, borderRadius: 8, padding: '0 10px', marginBottom: '10px', fontSize: '14px', border: '1px solid #dcdfe6', backgroundColor: '#F7F8FA' }}
                                suffix={otpVerified && <CheckCircleOutlined style={{ color: 'green' }} />}
                            />
                        </Form.Item>
                    )}

                    <label htmlFor="email" style={{ display: 'block', fontSize: '14px', marginBottom: '8px' }}>Email</label>
                    <Form.Item
                        name="email"
                        rules={[{ required: true, message: 'Please enter your email!' }, { type: 'email', message: 'Please enter a valid email!' }]}
                    >
                        <Input
                            prefix={<MailOutlined />}
                            placeholder="Email"
                            style={{ height: 40, borderRadius: 8, padding: '0 10px', marginBottom: '10px', fontSize: '14px', border: '1px solid #dcdfe6', backgroundColor: '#F7F8FA' }}
                        />
                    </Form.Item>

                    <label htmlFor="password" style={{ display: 'block', fontSize: '14px', marginBottom: '8px' }}>Password</label>
                    <Form.Item
                        name="password"
                        rules={[{ required: true, message: 'Please enter your password!' }]}
                    >
                        <Input.Password
                            prefix={<LockOutlined />}
                            placeholder="Password"
                            onChange={onPasswordChange}
                            style={{ height: 40, borderRadius: 8, padding: '0 10px', marginBottom: '10px', fontSize: '14px', border: '1px solid #dcdfe6', backgroundColor: '#F7F8FA' }}
                        />
                    </Form.Item>

                    <Row gutter={16} style={{ marginBottom: 16 }}>
                        <Col span={12}>
                            <div style={{ color: passwordCriteria.length ? 'green' : 'red' }}>
                                {passwordCriteria.length && <CheckCircleOutlined />} 8-50 characters
                            </div>
                            <div style={{ color: passwordCriteria.number ? 'green' : 'red' }}>
                                {passwordCriteria.number && <CheckCircleOutlined />} One number
                            </div>
                            <div style={{ color: passwordCriteria.symbol ? 'green' : 'red' }}>
                                {passwordCriteria.symbol && <CheckCircleOutlined />} One symbol (@$!%*?&)
                            </div>
                        </Col>
                        <Col span={12}>
                            <div style={{ color: passwordCriteria.uppercase ? 'green' : 'red' }}>
                                {passwordCriteria.uppercase && <CheckCircleOutlined />} One uppercase letter
                            </div>
                            <div style={{ color: passwordCriteria.lowercase ? 'green' : 'red' }}>
                                {passwordCriteria.lowercase && <CheckCircleOutlined />} One lowercase letter
                            </div>
                        </Col>
                    </Row>

                    <label htmlFor="confirm" style={{ display: 'block', fontSize: '14px', marginBottom: '8px' }}>Confirm password</label>
                    <Form.Item
                        name="confirm"
                        rules={[{ required: true, message: 'Please confirm your password!' }]}
                    >
                        <Input.Password
                            prefix={<LockOutlined />}
                            placeholder="Confirm Password"
                            style={{ height: 40, borderRadius: 8, padding: '0 10px', marginBottom: '10px', fontSize: '14px', border: '1px solid #dcdfe6', backgroundColor: '#F7F8FA' }}
                        />
                    </Form.Item>

                    <Form.Item>
                        <Button type="primary" htmlType="submit" block loading={loading} style={{ background: 'linear-gradient(45deg, #F1C451, #F1C451)', color: '#fff', fontWeight: 'bold', fontSize: 16, height: '40px', marginTop: 2, padding: '12px 0', borderRadius: '8px', boxShadow: '0 6px 15px rgba(0, 0, 0, 0.1)', transition: 'background 0.3s ease, box-shadow 0.3s ease', '&:hover': { background: 'linear-gradient(45deg, #4CAF50, #2E8B57)', boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)' }, '&:active': { background: '#4CAF50' } }}>
                            Sign Up
                        </Button>
                    </Form.Item>

                    <Box sx={{ marginTop: 3 }}>
                        <Link href="/" sx={{ display: 'block', color: '#2E8B57', textDecoration: 'none', marginTop: 1, fontWeight: 'bold', fontSize: '16px', textAlign: 'center', '&:hover': { textDecoration: 'underline', color: '#4CAF50' } }}>
                            Already have an account? Login
                        </Link>
                    </Box>
                </Form>
            </div>
        </div>
    );
};

export default SignUp;
